import { storableError } from '../../util/errors';
import { parse, getValidInboxSort, INBOX_STATES } from '../../util/urlHelpers';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

// ================ Action types ================ //

export const FETCH_ORDERS_OR_SALES_REQUEST = 'app/InboxPage/FETCH_ORDERS_OR_SALES_REQUEST';
export const FETCH_ORDERS_OR_SALES_SUCCESS = 'app/InboxPage/FETCH_ORDERS_OR_SALES_SUCCESS';
export const FETCH_ORDERS_OR_SALES_ERROR = 'app/InboxPage/FETCH_ORDERS_OR_SALES_ERROR';

export const FETCH_SALES_STATS_REQUEST = 'app/InboxPage/FETCH_SALES_STATS_REQUEST';
export const FETCH_SALES_STATS_SUCCESS = 'app/InboxPage/FETCH_SALES_STATS_SUCCESS';
export const FETCH_SALES_STATS_ERROR = 'app/InboxPage/FETCH_SALES_STATS_ERROR';

// ================ Reducer ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const initialState = {
  fetchInProgress: false,
  fetchOrdersOrSalesError: null,
  // The latest orders/sales request; an older answer is dropped.
  fetchRequest: null,
  pagination: null,
  transactionRefs: [],
  // The seller's counts on the sales tab: { needToShipCount, shippedCount, completedCount }
  salesStats: null,
  salesStatsInProgress: false,
  salesStatsError: null,
  salesStatsRequest: null,
};

export default function inboxPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_ORDERS_OR_SALES_REQUEST:
      return {
        ...state,
        fetchInProgress: true,
        fetchOrdersOrSalesError: null,
        fetchRequest: payload,
      };
    case FETCH_ORDERS_OR_SALES_SUCCESS: {
      if (action.meta?.request !== state.fetchRequest) return state;
      const transactions = payload.data.data;
      return {
        ...state,
        fetchInProgress: false,
        transactionRefs: entityRefs(transactions),
        pagination: payload.data.meta,
      };
    }
    case FETCH_ORDERS_OR_SALES_ERROR:
      if (action.meta?.request !== state.fetchRequest) return state;
      console.error(payload); // eslint-disable-line
      return { ...state, fetchInProgress: false, fetchOrdersOrSalesError: payload };

    case FETCH_SALES_STATS_REQUEST:
      return {
        ...state,
        salesStatsInProgress: true,
        salesStatsError: null,
        salesStatsRequest: payload,
      };
    case FETCH_SALES_STATS_SUCCESS:
      return action.meta?.request !== state.salesStatsRequest
        ? state
        : { ...state, salesStatsInProgress: false, salesStats: payload };
    case FETCH_SALES_STATS_ERROR:
      return action.meta?.request !== state.salesStatsRequest
        ? state
        : { ...state, salesStatsInProgress: false, salesStats: null, salesStatsError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchOrdersOrSalesRequest = request => ({
  type: FETCH_ORDERS_OR_SALES_REQUEST,
  payload: request,
});
const fetchOrdersOrSalesSuccess = (response, request) => ({
  type: FETCH_ORDERS_OR_SALES_SUCCESS,
  payload: response,
  meta: { request },
});
const fetchOrdersOrSalesError = (e, request) => ({
  type: FETCH_ORDERS_OR_SALES_ERROR,
  error: true,
  payload: e,
  meta: { request },
});

const fetchSalesStatsRequest = request => ({ type: FETCH_SALES_STATS_REQUEST, payload: request });
const fetchSalesStatsSuccess = (stats, request) => ({
  type: FETCH_SALES_STATS_SUCCESS,
  payload: stats,
  meta: { request },
});
const fetchSalesStatsError = (e, request) => ({
  type: FETCH_SALES_STATS_ERROR,
  error: true,
  payload: e,
  meta: { request },
});

// ================ Thunks ================ //

const INBOX_PAGE_SIZE = 10;

// How many sales are waiting to ship, on their way, and done: the boxes over the
// sales list. Each box counts the orders its inbox filter lists (util/urlHelpers).
let salesStatsRequests = 0;
export const fetchSalesStats = () => (dispatch, getState, sdk) => {
  salesStatsRequests += 1;
  const request = salesStatsRequests;
  dispatch(fetchSalesStatsRequest(request));

  const countOf = states =>
    sdk.transactions
      .query({ only: 'sale', states: states.join(','), page: 1, perPage: 1 })
      .then(response => response.data.meta.totalItems || 0);

  return Promise.all([
    countOf(INBOX_STATES.needToShip),
    countOf(INBOX_STATES.shipped),
    countOf(INBOX_STATES.completed),
  ])
    .then(([needToShipCount, shippedCount, completedCount]) => {
      dispatch(fetchSalesStatsSuccess({ needToShipCount, shippedCount, completedCount }, request));
    })
    .catch(e => {
      dispatch(fetchSalesStatsError(storableError(e), request));
    });
};

let inboxRequests = 0;
export const loadData = (params, search) => (dispatch, getState, sdk) => {
  const { tab } = params;

  const onlyFilterValues = {
    orders: 'order',
    sales: 'sale',
  };

  const onlyFilter = onlyFilterValues[tab];
  if (!onlyFilter) {
    return Promise.reject(new Error(`Invalid tab for InboxPage: ${tab}`));
  }

  inboxRequests += 1;
  const request = inboxRequests;
  dispatch(fetchOrdersOrSalesRequest(request));

  const { page = 1, sort } = parse(search);
  const sortOrFilterParams = getValidInboxSort(sort);

  const apiQueryParams = {
    only: onlyFilter,
    // No lastTransitions filter: it listed every transition of every process
    // (it filtered nothing), and with process v76 that list made the URL so
    // long that Sharetribe refused it (431 Request Header Fields Too Large).
    include: [
      'listing',
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'booking',
    ],
    'fields.transaction': [
      'processName',
      'processVersion',
      'state',
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'lineItems',
      'protectedData',
      'metadata',
    ],
    'fields.listing': ['title', 'availabilityPlan', 'publicData.listingType'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.publicData'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
    page,
    perPage: INBOX_PAGE_SIZE,
    ...sortOrFilterParams,
  };

  const list = sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(fetchOrdersOrSalesSuccess(response, request));
      return response;
    })
    .catch(e => {
      dispatch(fetchOrdersOrSalesError(storableError(e), request));
      throw e;
    });

  // The sales tab opens with the seller's counts; a failed count shows as unknown, not as 0
  return tab === 'sales'
    ? Promise.all([list, dispatch(fetchSalesStats())]).then(([response]) => response)
    : list;
};
